import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, LayoutGrid, List, RotateCcw, Users as UsersIcon, Sparkles } from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import PlayerCard from '../components/PlayerCard'
import PlayerForm from '../components/PlayerForm'
import RenewSeasonModal from '../components/RenewSeasonModal'
import playerService from '../services/playerService'
import matchService from '../services/matchService'
import { useAuth } from '../context/AuthContext'
import { useFreeStatus } from '../hooks/useFreeStatus'
import { useSeason, formatSeasonShort } from '../context/SeasonContext'
import { T, globalStyles } from '../theme'
import { POSITION_COLORS } from '../constants/positionColors'
import { hasScore, resultLetter } from '../utils/matchResult'

const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'
function authHeaders() {
  const token = localStorage.getItem('insightball_token')
  return token ? { 'Authorization': `Bearer ${token}` } : {}
}

const POS_ORDER = ['Gardien', 'Défenseur', 'Milieu', 'Attaquant']

const FILTER_BTNS = [
  { key: 'all',       label: 'Tous' },
  { key: 'Gardien',   label: 'GB' },
  { key: 'Défenseur', label: 'DEF' },
  { key: 'Milieu',    label: 'MIL' },
  { key: 'Attaquant', label: 'ATT' },
]

const STAT_ACCENTS = [T.accentStat1, T.accentStat2, T.accentStat3, T.accentStat4]

function calculateAge(birthDate) {
  if (!birthDate) return null
  const b = new Date(birthDate)
  if (Number.isNaN(b.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - b.getFullYear()
  const m = now.getMonth() - b.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--
  return age
}

function isRegularStarter(player) {
  return (player?.matches_played ?? 0) >= 5
}

function getLastFiveResults(player, matches) {
  if (!player || (player.matches_played ?? 0) === 0) return []
  if (!Array.isArray(matches) || matches.length === 0) return []
  const scored = matches
    .filter(hasScore)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(-5)
  // Cette page code la forme en W/N/L, pas en V/N/D.
  return scored.map(m => ({ V: 'W', N: 'N', D: 'L' })[resultLetter(m)])
}

const RESULT_COLOR = { W: '#3DBF7A', N: '#4F8EF7', L: '#D32F2F' }
const FORM_EMPTY   = T.rule

function FormStrip({ results, count = 5, w = 5, h = 14 }) {
  const cells = []
  for (let i = 0; i < count; i++) {
    const r = results[i]
    cells.push(
      <div key={i} style={{ width: w, height: h, background: r ? RESULT_COLOR[r] : FORM_EMPTY }} />
    )
  }
  return <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>{cells}</div>
}


function PlayerManagement() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isExpired: isFreeExpired } = useFreeStatus()
  const { selectedSeason, isNextSeason, isPastSeason } = useSeason()

  const [players, setPlayers]               = useState([])
  const [matches, setMatches]               = useState([])
  const [filteredPlayers, setFilteredPlayers] = useState([])
  const [loading, setLoading]               = useState(true)
  const [isMobile, setIsMobile]             = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  const [viewMode, setViewMode]             = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'grid' : 'table'
  )

  const [showRenewModal, setShowRenewModal] = useState(false)
  const [previousSeasonPlayers, setPreviousSeasonPlayers] = useState([])
  const [previousSeason, setPreviousSeason] = useState(null)

  const [isFormOpen, setIsFormOpen]         = useState(false)
  const [editingPlayer, setEditingPlayer]   = useState(null)
  const [selectedPosition, setSelectedPosition] = useState('all')
  const [searchQuery, setSearchQuery]       = useState('')

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => { loadData() }, [selectedSeason])
  useEffect(() => { filterPlayers() }, [players, selectedPosition, searchQuery])

  useEffect(() => {
    if (loading) return
    if (players.length > 0) { setPreviousSeasonPlayers([]); setPreviousSeason(null); return }
    const [start] = (selectedSeason || '').split('-').map(Number)
    if (!start) return
    const prev = `${start - 1}-${start}`
    setPreviousSeason(prev)
    loadPreviousSeasonPlayers(prev)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, players.length, selectedSeason])

  const loadData = async () => {
    try {
      setLoading(true)
      const data = await playerService.getPlayers({ season: selectedSeason })
      setPlayers(data)
    } catch (e) { console.error(e) } finally { setLoading(false) }
    try {
      const m = await matchService.getMatches({ limit: 50 })
      setMatches(Array.isArray(m) ? m : (m?.items ?? []))
    } catch { setMatches([]) }
  }

  const loadPreviousSeasonPlayers = async (season) => {
    try {
      const res = await fetch(`${API}/players/?season=${encodeURIComponent(season)}`, { headers: authHeaders() })
      if (!res.ok) { setPreviousSeasonPlayers([]); return }
      const data = await res.json()
      setPreviousSeasonPlayers(Array.isArray(data) ? data : [])
    } catch { setPreviousSeasonPlayers([]) }
  }

  const handleRenewSuccess = async () => { setShowRenewModal(false); await loadData() }

  const filterPlayers = () => {
    let f = [...players]
    if (selectedPosition !== 'all') f = f.filter(p => p.position === selectedPosition)
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      f = f.filter(p => p.name.toLowerCase().includes(q) || p.number?.toString().includes(searchQuery))
    }
    setFilteredPlayers(f)
  }

  const handleAddPlayer    = async (data) => { await playerService.createPlayer(data); await loadData(); setIsFormOpen(false) }
  const handleEditPlayer   = async (data) => { await playerService.updatePlayer(editingPlayer.id, data); await loadData(); setEditingPlayer(null); setIsFormOpen(false) }
  const handleDeletePlayer = async (player) => {
    if (!confirm(`Supprimer ${player.name} ?`)) return
    try { await playerService.deletePlayer(player.id); await loadData() }
    catch { alert('Erreur lors de la suppression') }
  }
  const openEdit = (p) => { setEditingPlayer(p); setIsFormOpen(true) }

  const effectiveView = isMobile ? 'grid' : viewMode
  const seasonLabel = formatSeasonShort(selectedSeason)

  const total = players.length
  const regularsLabel = players.filter(p => (p.matches_played ?? 0) >= 3).length
  const posCount = (pos) => players.filter(p => p.position === pos).length

  return (
    <DashboardLayout>
      <style>{`${globalStyles}
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        .pm-row:hover { background: ${T.bgAlt} !important; }
        ::placeholder { color: ${T.muted}; }
      `}</style>

      <div style={{ padding: '24px 24px 60px', maxWidth: 1280, margin: '0 auto' }}>

        {/* ── HEADER ── */}
        <div style={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-end',
          gap: isMobile ? 16 : 0,
          marginBottom: 24, paddingBottom: 20,
          borderBottom: `0.5px solid ${T.rule}`,
        }}>
          <div>
            <p style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>
              Effectif · Saison {seasonLabel}
            </p>
            <h1 style={{
              fontFamily: T.display, fontSize: 'clamp(28px, 5vw, 36px)', fontWeight: 800,
              color: T.ink, textTransform: 'uppercase', letterSpacing: '-.01em',
              lineHeight: 1, margin: 0,
            }}>
              Vos joueurs.
            </h1>
          </div>
          {!isPastSeason && (
            <button
              onClick={() => isFreeExpired ? null : setIsFormOpen(true)}
              disabled={isFreeExpired}
              title={isFreeExpired ? "Ton essai Free est terminé. Passe en Coach+ pour ajouter des joueurs." : undefined}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '0 22px', minHeight: 44,
                background: isFreeExpired ? T.muted2 : T.dark,
                color: '#FFFFFF', border: 'none',
                fontFamily: T.mono, fontSize: 11, fontWeight: 600,
                cursor: isFreeExpired ? 'not-allowed' : 'pointer',
                borderRadius: T.radius,
                width: isMobile ? '100%' : 'auto',
              }}
            >
              <Plus size={14} /> Ajouter un joueur
            </button>
          )}
        </div>

        {/* ── BANDEAU KPI ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(5,1fr)',
          gap: 8, marginBottom: 20,
        }}>
          {/* Total */}
          <div style={{
            background: T.surface, border: `0.5px solid ${T.rule}`,
            borderTop: `2.5px solid ${STAT_ACCENTS[0]}`,
            borderRadius: T.radius, padding: '16px 18px',
            gridColumn: isMobile ? 'span 2' : 'span 1',
          }}>
            <p style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>
              Effectif total
            </p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontFamily: T.display, fontSize: 36, fontWeight: 800, color: T.ink, lineHeight: 1 }}>{total}</span>
              <span style={{ fontFamily: T.mono, fontSize: 10, color: T.muted }}>joueurs</span>
            </div>
            <p style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, marginTop: 6 }}>
              {regularsLabel} titulaire{regularsLabel > 1 ? 's' : ''} régulier{regularsLabel > 1 ? 's' : ''}
            </p>
          </div>

          {/* 4 postes */}
          {POS_ORDER.map((pos, i) => {
            const { abbr, color } = POSITION_COLORS[pos]
            const n = posCount(pos)
            const pct = total > 0 ? (n / total) * 100 : 0
            return (
              <div key={pos} style={{
                background: T.surface, border: `0.5px solid ${T.rule}`,
                borderTop: `2.5px solid ${STAT_ACCENTS[(i + 1) % 4]}`,
                borderRadius: T.radius, padding: '16px 18px',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <div style={{ width: 6, height: 6, borderRadius: 2, background: color, flexShrink: 0 }} />
                    <span style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', color: T.muted }}>{abbr}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontFamily: T.display, fontSize: 28, fontWeight: 800, color: T.ink, lineHeight: 1 }}>{n}</span>
                    <span style={{ fontFamily: T.mono, fontSize: 10, color: T.muted }}>/ {total}</span>
                  </div>
                </div>
                <div style={{ marginTop: 10, height: 3, background: T.rule, borderRadius: 2 }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
                </div>
              </div>
            )
          })}
        </div>

        {/* ── FILTRES ── */}
        <div style={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          gap: 8, marginBottom: 20,
          paddingBottom: 16, borderBottom: `0.5px solid ${T.rule}`,
        }}>
          {/* Recherche */}
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 10,
            background: T.surface, border: `0.5px solid ${T.rule}`,
            borderRadius: T.radius, padding: '0 14px', minHeight: 44,
          }}>
            <Search size={14} color={T.muted} style={{ flexShrink: 0 }} />
            <input
              type="text" placeholder="Nom ou numéro..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              style={{
                flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none',
                color: T.ink, fontFamily: T.mono, fontSize: 13, padding: '0',
              }}
            />
          </div>

          {/* Filtres poste (underline style) */}
          <div style={{
            display: 'flex', gap: 0,
            borderBottom: `0.5px solid ${T.rule}`,
            overflowX: 'auto',
          }}>
            {FILTER_BTNS.map(b => {
              const active = selectedPosition === b.key
              return (
                <button key={b.key} onClick={() => setSelectedPosition(b.key)} style={{
                  padding: '10px 14px',
                  border: 'none',
                  background: 'transparent',
                  color: active ? T.ink : T.muted,
                  fontFamily: T.mono, fontSize: 11, fontWeight: active ? 600 : 400,
                  cursor: 'pointer',
                  borderBottom: active ? `2px solid ${T.ink}` : '2px solid transparent',
                  marginBottom: -1,
                  whiteSpace: 'nowrap',
                }}>
                  {b.label}
                </button>
              )
            })}
          </div>

          {/* Toggle vue (masqué mobile) */}
          {!isMobile && (
            <div style={{ display: 'flex', gap: 1, background: T.rule, borderRadius: T.radius, overflow: 'hidden' }}>
              {[
                { key: 'grid',  Icon: LayoutGrid },
                { key: 'table', Icon: List },
              ].map(({ key, Icon }) => {
                const active = viewMode === key
                return (
                  <button key={key} onClick={() => setViewMode(key)} aria-label={key} style={{
                    padding: '10px 14px', minHeight: 44,
                    background: active ? T.dark : T.surface,
                    color: active ? '#FFFFFF' : T.muted,
                    border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  }}>
                    <Icon size={15} />
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* ── LISTE ── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${T.rule}`, borderTopColor: T.ink, borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto 14px' }} />
            <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted }}>Chargement...</p>
          </div>
        ) : filteredPlayers.length === 0 ? (
          (() => {
            const noFilters = selectedPosition === 'all' && !searchQuery
            const seasonIsEmpty = players.length === 0 && noFilters
            if (seasonIsEmpty && previousSeasonPlayers.length > 0) {
              return (
                <div style={{
                  background: T.surface, border: `0.5px solid ${T.rule}`,
                  borderTop: `2px solid ${T.dark}`, borderRadius: T.radius,
                  padding: '48px 24px', textAlign: 'center',
                }}>
                  <div style={{
                    width: 56, height: 56, background: T.bgAlt, border: `0.5px solid ${T.rule}`,
                    borderRadius: T.radius,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
                  }}>
                    <RotateCcw size={24} color={T.ink} />
                  </div>
                  <div style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.18em', textTransform: 'uppercase', color: T.muted, marginBottom: 12 }}>
                    Nouvelle saison
                  </div>
                  <h3 style={{ fontFamily: T.display, fontSize: isMobile ? 26 : 32, fontWeight: 800, textTransform: 'uppercase', color: T.ink, marginBottom: 12, lineHeight: 1 }}>
                    Prêt pour {formatSeasonShort(selectedSeason)} ?
                  </h3>
                  <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, marginBottom: 28, lineHeight: 1.7, maxWidth: 460, margin: '0 auto 28px' }}>
                    Tu as <strong style={{ color: T.ink }}>{previousSeasonPlayers.length} joueur{previousSeasonPlayers.length > 1 ? 's' : ''}</strong> dans ton effectif {formatSeasonShort(previousSeason)}. Renouvelle-le en quelques clics.
                  </p>
                  {!isPastSeason && (
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button onClick={() => setShowRenewModal(true)} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '0 22px', minHeight: 44,
                        background: T.dark, color: '#FFFFFF',
                        fontFamily: T.mono, fontSize: 11, fontWeight: 600,
                        border: 'none', cursor: 'pointer', borderRadius: T.radius,
                      }}>
                        <Sparkles size={14} /> Renouveler l'effectif
                      </button>
                      <button onClick={() => setIsFormOpen(true)} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '0 22px', minHeight: 44,
                        background: 'transparent', color: T.ink,
                        fontFamily: T.mono, fontSize: 11, fontWeight: 500,
                        border: `0.5px solid ${T.rule}`, cursor: 'pointer', borderRadius: T.radius,
                      }}>
                        <Plus size={14} /> Ajouter manuellement
                      </button>
                    </div>
                  )}
                </div>
              )
            }
            if (seasonIsEmpty) {
              return (
                <div style={{
                  background: T.surface, border: `0.5px solid ${T.rule}`,
                  borderTop: `2px solid ${T.dark}`, borderRadius: T.radius,
                  padding: '80px 24px', textAlign: 'center',
                }}>
                  <div style={{
                    width: 56, height: 56, background: T.bgAlt, border: `0.5px solid ${T.rule}`,
                    borderRadius: T.radius,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
                  }}>
                    <UsersIcon size={24} color={T.ink} />
                  </div>
                  <h3 style={{ fontFamily: T.display, fontSize: 28, fontWeight: 800, textTransform: 'uppercase', color: T.ink, marginBottom: 10 }}>
                    {isNextSeason ? 'Prépare cette saison' : 'Aucun joueur'}
                  </h3>
                  <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, marginBottom: 28, lineHeight: 1.7, maxWidth: 420, margin: '0 auto 28px' }}>
                    {isNextSeason ? `Commence à constituer ton effectif pour la saison ${formatSeasonShort(selectedSeason)}.` : "Commence par ajouter tes joueurs à l'effectif"}
                  </p>
                  {!isPastSeason && (
                    <button onClick={() => setIsFormOpen(true)} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '0 28px', minHeight: 44,
                      background: T.dark, color: '#FFFFFF',
                      fontFamily: T.mono, fontSize: 11, fontWeight: 600,
                      border: 'none', cursor: 'pointer', borderRadius: T.radius,
                    }}>
                      <Plus size={14} /> Ajouter un joueur
                    </button>
                  )}
                </div>
              )
            }
            return (
              <div style={{
                background: T.surface, border: `0.5px solid ${T.rule}`,
                borderTop: `2px solid ${T.rule}`, borderRadius: T.radius,
                padding: '60px 24px', textAlign: 'center',
              }}>
                <div style={{
                  width: 48, height: 48, background: T.bgAlt, border: `0.5px solid ${T.rule}`,
                  borderRadius: T.radius,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
                }}>
                  <Search size={20} color={T.muted} />
                </div>
                <h3 style={{ fontFamily: T.display, fontSize: 24, fontWeight: 800, textTransform: 'uppercase', color: T.ink, marginBottom: 8 }}>Aucun résultat</h3>
                <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, lineHeight: 1.7 }}>Modifie tes critères de filtre.</p>
              </div>
            )
          })()
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 22 : 28 }}>
            {POS_ORDER.map((position, i) => {
              const pos = filteredPlayers
                .filter(p => p.position === position)
                .sort((a, b) => (a.number ?? 999) - (b.number ?? 999))
              if (pos.length === 0) return null
              const { label } = POSITION_COLORS[position]
              return (
                <div key={position} style={{ animation: `fadeIn .35s ease ${i * 70}ms both` }}>
                  {/* Section header */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    paddingBottom: 12, marginBottom: 12,
                    borderBottom: `0.5px solid ${T.rule}`,
                  }}>
                    <h2 style={{ fontFamily: T.display, fontSize: 16, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: T.ink, margin: 0 }}>
                      {label}
                    </h2>
                    <span style={{
                      fontFamily: T.mono, fontSize: 9, fontWeight: 600,
                      padding: '2px 8px', borderRadius: 20,
                      background: T.bgAlt, border: `0.5px solid ${T.rule}`,
                      color: T.muted,
                    }}>
                      {pos.length}
                    </span>
                    <span style={{ marginLeft: 'auto', fontFamily: T.mono, fontSize: 9, color: T.muted }}>
                      trié par numéro
                    </span>
                  </div>

                  {effectiveView === 'grid' ? (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 150 : 170}px, 1fr))`,
                      gap: 8,
                    }}>
                      {pos.map(p => (
                        <PlayerCard
                          key={p.id}
                          player={p}
                          form={getLastFiveResults(p, matches)}
                          onEdit={isPastSeason ? undefined : openEdit}
                          onDelete={isPastSeason ? undefined : handleDeletePlayer}
                        />
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      background: T.surface, border: `0.5px solid ${T.rule}`,
                      borderRadius: T.radius, overflow: 'hidden',
                    }}>
                      {/* Table header */}
                      <div style={{
                        display: 'grid', gridTemplateColumns: '40px 1fr 70px 60px 60px 50px 50px 60px',
                        padding: '8px 18px', background: T.bgAlt,
                        borderBottom: `0.5px solid ${T.rule}`,
                        fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', color: T.muted,
                      }}>
                        <span>#</span>
                        <span>Joueur</span>
                        <span>Poste</span>
                        <span style={{ textAlign: 'right' }}>MJ</span>
                        <span style={{ textAlign: 'right' }}>Min</span>
                        <span style={{ textAlign: 'right' }}>B</span>
                        <span style={{ textAlign: 'right' }}>PD</span>
                        <span style={{ textAlign: 'right' }}>Forme</span>
                      </div>
                      {pos.map((p, idx) => {
                        const mj = p.matches_played ?? 0
                        const min = p.total_minutes ?? 0
                        const b = p.goals ?? 0
                        const pd = p.assists ?? 0
                        const reg = isRegularStarter(p)
                        const form = getLastFiveResults(p, matches)
                        const posC = POSITION_COLORS[position]
                        return (
                          <div
                            key={p.id}
                            className="pm-row"
                            onClick={() => navigate(`/dashboard/player/${p.id}`)}
                            style={{
                              display: 'grid', gridTemplateColumns: '40px 1fr 70px 60px 60px 50px 50px 60px',
                              padding: '12px 18px', alignItems: 'center', cursor: 'pointer',
                              background: T.surface,
                              borderBottom: idx < pos.length - 1 ? `0.5px solid ${T.rule}` : 'none',
                            }}
                          >
                            <span style={{
                              fontFamily: T.display, fontSize: 16, fontWeight: 800,
                              color: reg ? T.ink : T.muted,
                            }}>
                              {p.number ?? '--'}
                            </span>
                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <span style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 600, color: T.ink }}>{p.name}</span>
                              {p.category && (
                                <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, letterSpacing: '.05em', marginLeft: 8 }}>{p.category}</span>
                              )}
                            </span>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center',
                              padding: '3px 9px', borderRadius: 20,
                              background: posC?.bg || T.bgAlt,
                              fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.08em',
                              color: posC?.color || T.muted,
                              width: 'fit-content',
                            }}>
                              {posC?.abbr || position}
                            </span>
                            <span style={{ textAlign: 'right', fontFamily: T.mono, fontSize: 13, fontWeight: mj > 0 ? 600 : 400, color: mj > 0 ? T.ink : T.muted }}>{mj}</span>
                            <span style={{ textAlign: 'right', fontFamily: T.mono, fontSize: 13, color: min > 0 ? T.ink : T.muted }}>{min > 0 ? `${min}'` : '—'}</span>
                            <span style={{ textAlign: 'right', fontFamily: T.mono, fontSize: 13, fontWeight: b > 0 ? 700 : 400, color: b > 0 ? T.green : T.muted }}>{b > 0 ? b : '—'}</span>
                            <span style={{ textAlign: 'right', fontFamily: T.mono, fontSize: 13, color: pd > 0 ? T.blue : T.muted }}>{pd > 0 ? pd : '—'}</span>
                            <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              {mj === 0
                                ? <span style={{ fontFamily: T.mono, fontSize: 9, fontStyle: 'italic', color: T.muted }}>N/A</span>
                                : <FormStrip results={form} w={5} h={14} />}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

      </div>

      <PlayerForm isOpen={isFormOpen} onClose={() => { setIsFormOpen(false); setEditingPlayer(null) }} onSubmit={editingPlayer ? handleEditPlayer : handleAddPlayer} player={editingPlayer} managedCategory={user?.managed_category} />

      <RenewSeasonModal
        isOpen={showRenewModal}
        onClose={() => setShowRenewModal(false)}
        sourceSeason={previousSeason}
        targetSeason={selectedSeason}
        sourcePlayers={previousSeasonPlayers}
        onSuccess={handleRenewSuccess}
      />
    </DashboardLayout>
  )
}

export default PlayerManagement
